import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Star, Moon, Sun, Sparkles, Calendar, Newspaper, Store, 
  MapPin, ExternalLink, Clock, TrendingUp, Zap, Heart,
  ChevronRight, ChevronLeft, Filter, Search, Crown, Shield, Gem, Rss, ArrowLeft, BookOpen, Send, CheckCircle, Home, Compass
} from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const directoryListings = [
  { id: 1, name: "Crystal Visions Healing Center", category: "Healing Center", location: "Los Angeles, CA", featured: true, rating: 4.9, reviews: 127, services: ["Crystal Healing", "Reiki", "Sound Therapy"] },
  { id: 2, name: "Sacred Stones Boutique", category: "Crystal Shop", location: "Sedona, AZ", featured: true, rating: 4.8, reviews: 89, services: ["Rare Crystals", "Custom Jewelry", "Readings"] },
  { id: 3, name: "Moonlight Mystics", category: "Psychic Services", location: "New Orleans, LA", featured: true, rating: 5.0, reviews: 203, services: ["Tarot", "Mediumship", "Past Life"] },
  { id: 4, name: "Chakra Alignment Studio", category: "Wellness Center", location: "Austin, TX", featured: false, rating: 4.7, reviews: 56, services: ["Yoga", "Meditation", "Energy Work"] },
  { id: 5, name: "The Gemstone Emporium", category: "Crystal Shop", location: "Portland, OR", featured: false, rating: 4.6, reviews: 42, services: ["Wholesale", "Specimens", "Tumbled Stones"] },
];

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const MetaphysicalHubPage = () => {
  usePageTitle('Metaphysical Hub');
  const [activeTab, setActiveTab] = useState('guidance');
  const [selectedSign, setSelectedSign] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [cosmicData, setCosmicData] = useState(null);
  const [events, setEvents] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventFormSubmitted, setEventFormSubmitted] = useState(false);
  const [eventSubmitting, setEventSubmitting] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '', description: '', date: '', time: '', event_type: 'Online',
    category: 'Workshop', host: '', contact_email: '', free: false, price: 0,
  });
  const [activeBaguaZone, setActiveBaguaZone] = useState(null);
  const [showFengShuiForm, setShowFengShuiForm] = useState(false);
  const [fengShuiSubmitted, setFengShuiSubmitted] = useState(false);
  const [fengShuiSubmitting, setFengShuiSubmitting] = useState(false);
  const [fsForm, setFsForm] = useState({
    fullName: '', email: '', phone: '', address: '', city: '', state: '', zipCode: '',
    squareFootage: 2200, homeType: 'Single Family', rooms: '', primaryGoals: '',
    currentChallenges: '', preferredDate: '', howHeard: '', additionalNotes: '',
  });

  useEffect(() => {
    fetch(`${API_URL}/api/articles`).then(r => r.json()).then(setArticles).catch(() => {});
    fetch(`${API_URL}/api/cosmic-guidance`).then(r => r.json()).then(setCosmicData).catch(() => {});
    fetch(`${API_URL}/api/events`).then(r => r.json()).then(setEvents).catch(() => {});
  }, []);

  const openArticle = async (slug) => {
    setLoadingArticle(true);
    try {
      const res = await fetch(`${API_URL}/api/articles/${slug}`);
      const data = await res.json();
      setSelectedArticle(data);
    } catch { setSelectedArticle(null); }
    finally { setLoadingArticle(false); }
  };

  const closeArticle = () => setSelectedArticle(null);
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const handleEventChange = (e) => setEventForm({ ...eventForm, [e.target.name]: e.target.value });
  const handleEventSubmit = async (e) => {
    e.preventDefault();
    setEventSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/events/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventForm),
      });
      if (res.ok) {
        toast.success('Event submitted! We will review it within 48 hours.');
        setEventFormSubmitted(true);
      } else throw new Error();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally { setEventSubmitting(false); }
  };

  const handleFsChange = (e) => setFsForm({ ...fsForm, [e.target.name]: e.target.value });
  const handleFsSubmit = async (e) => {
    e.preventDefault();
    setFengShuiSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/fengshui/consultation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...fsForm, squareFootage: parseInt(fsForm.squareFootage) }),
      });
      if (res.ok) {
        toast.success('Consultation request submitted! Our team will contact you within 24 hours.');
        setFengShuiSubmitted(true);
      } else throw new Error();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally { setFengShuiSubmitting(false); }
  };

  const getDeposit = (sqft) => {
    const s = parseInt(sqft) || 2200;
    if (s <= 2200) return 6000;
    if (s <= 3500) return 9000;
    if (s <= 5000) return 14000;
    return 20000;
  };

  // Calendar helpers
  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();
  
  const prevMonth = () => {
    if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear(y => y - 1); }
    else setCalendarMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear(y => y + 1); }
    else setCalendarMonth(m => m + 1);
  };

  const getEventsForDate = (day) => {
    const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const eventCategories = ['All', ...new Set(events.map(e => e.category).filter(Boolean))];
  const filteredEvents = selectedCategory === 'All' ? events : events.filter(e => e.category === selectedCategory);
  const sortedEvents = [...filteredEvents].sort((a, b) => new Date(a.date) - new Date(b.date));
  const featuredEvents = events.filter(e => e.featured);

  const tabs = [
    { id: 'guidance', label: 'Daily Guidance', icon: Star },
    { id: 'fengshui', label: 'Feng Shui', icon: Compass },
    { id: 'news', label: 'Metaphysical News', icon: Newspaper },
    { id: 'directory', label: 'Directory', icon: Store },
    { id: 'events', label: 'Events', icon: Calendar },
  ];

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero */}
      <section className="relative py-20 px-6 bg-gradient-to-br from-[#1e1919] via-[#2a2030] to-[#1e1919] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#9D4EDD]/40 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#FFB800]/30 rounded-full blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
            <Sparkles className="w-4 h-4 text-[#c9a96e]" />
            <span className="text-sm text-white/90 tracking-widest uppercase">Your Spiritual Command Center</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            Metaphysical <span className="text-[#c9a96e]">Hub</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
            Daily cosmic guidance, breaking spiritual news, and the largest directory of 
            metaphysical businesses and practitioners. Your one-stop destination for conscious living.
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-white/60">
            {cosmicData && (
              <>
                <div className="text-center">
                  <Moon className="w-6 h-6 mx-auto mb-2 text-[#9D4EDD]" />
                  <span className="text-sm">{cosmicData.moon_phase}</span>
                  <span className="text-xs block text-white/40">{cosmicData.moon_illumination}% illuminated</span>
                </div>
                <div className="text-center">
                  <Gem className="w-6 h-6 mx-auto mb-2 text-[#FF6B9D]" />
                  <span className="text-sm">Crystal: {cosmicData.crystal_of_day.crystal}</span>
                </div>
                <div className="text-center">
                  <Sun className="w-6 h-6 mx-auto mb-2 text-[#FFB800]" />
                  <span className="text-sm">{cosmicData.day_of_week}</span>
                </div>
              </>
            )}
            <a href={`${API_URL}/api/rss`} target="_blank" rel="noopener noreferrer" className="text-center hover:text-[#c9a96e] transition-colors" data-testid="rss-feed-link">
              <Rss className="w-6 h-6 mx-auto mb-2 text-[#c9a96e]" />
              <span className="text-sm">RSS Feed</span>
            </a>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-[#e8e4de] shadow-sm">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex gap-2 overflow-x-auto py-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSelectedArticle(null); }}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm whitespace-nowrap transition-all ${
                  activeTab === tab.id 
                    ? 'bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white shadow-lg' 
                    : 'bg-[#f8f5ff] text-[#61525a] hover:bg-[#9D4EDD]/10'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-12">

        {/* ===== GUIDANCE TAB ===== */}
        {activeTab === 'guidance' && cosmicData && (
          <div className="space-y-8" data-testid="guidance-tab-content">
            {/* Moon Phase Card */}
            <div className="bg-gradient-to-r from-[#1e1919] to-[#2a2030] rounded-2xl p-8 text-white">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="relative">
                  <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#9D4EDD]/40 to-[#FFB800]/20 flex items-center justify-center">
                    <Moon className="w-14 h-14 text-[#c9a96e]" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 px-2 py-1 rounded-full bg-[#9D4EDD] text-xs font-bold">
                    {cosmicData.moon_illumination}%
                  </div>
                </div>
                <div className="text-center md:text-left flex-1">
                  <p className="text-sm text-white/50 uppercase tracking-widest mb-1">{cosmicData.date}</p>
                  <h2 className="font-serif text-3xl mb-2">{cosmicData.moon_phase}</h2>
                  <p className="text-white/60 max-w-lg">
                    {cosmicData.moon_phase === 'New Moon' && 'Set new intentions and plant seeds for the coming cycle. A powerful time for beginnings.'}
                    {cosmicData.moon_phase === 'Waxing Crescent' && 'Your intentions are taking root. Nurture your goals with positive action and belief.'}
                    {cosmicData.moon_phase === 'First Quarter' && 'A time of action and decision. Push through challenges and stay committed to your path.'}
                    {cosmicData.moon_phase === 'Waxing Gibbous' && 'Refine and adjust. Your efforts are building toward fruition. Trust the process.'}
                    {cosmicData.moon_phase === 'Full Moon' && 'Peak illumination brings clarity and completion. Charge your crystals and release what no longer serves you.'}
                    {cosmicData.moon_phase === 'Waning Gibbous' && 'Share your wisdom and express gratitude. A time for reflection and giving back.'}
                    {cosmicData.moon_phase === 'Last Quarter' && 'Release and let go. Forgive, cleanse, and make space for new energy to enter.'}
                    {cosmicData.moon_phase === 'Waning Crescent' && 'Rest, reflect, and surrender. Prepare your spirit for the new cycle ahead.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Crystal of the Day */}
            <div className="bg-gradient-to-r from-[#9D4EDD]/10 to-[#FF6B9D]/10 rounded-2xl p-8 border border-[#9D4EDD]/20">
              <div className="flex items-center gap-3 mb-4">
                <Gem className="w-8 h-8 text-[#9D4EDD]" />
                <h2 className="font-serif text-2xl text-[#1e1919]">Crystal of the Day</h2>
              </div>
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl" style={{ backgroundColor: `${cosmicData.crystal_of_day.color}20` }}>
                  {cosmicData.crystal_of_day.symbol}
                </div>
                <div>
                  <h3 className="font-serif text-3xl text-[#1e1919]">{cosmicData.crystal_of_day.crystal}</h3>
                  <p className="text-[#61525a]">Aligned with {cosmicData.crystal_of_day.name} energy &bull; {cosmicData.crystal_of_day.element} element</p>
                  <p className="text-sm text-[#9D4EDD] mt-2">Work with {cosmicData.crystal_of_day.crystal} today to amplify your intentions and align with cosmic flow.</p>
                </div>
              </div>
            </div>

            {/* Universal Message */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#e8e4de]">
              <div className="flex items-center gap-3 mb-4">
                <Star className="w-6 h-6 text-[#FFB800] fill-current" />
                <h2 className="font-serif text-xl text-[#1e1919]">Universal Message for Today</h2>
              </div>
              <p className="text-lg text-[#4A4A6A] leading-relaxed italic">"{cosmicData.daily_message}"</p>
            </div>

            {/* Zodiac Readings */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#e8e4de]">
              <h2 className="font-serif text-2xl text-[#1e1919] mb-6 text-center">Select Your Sign for Personalized Guidance</h2>
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-3">
                {cosmicData.zodiac_readings.map((sign) => (
                  <button
                    key={sign.name}
                    onClick={() => setSelectedSign(selectedSign?.name === sign.name ? null : sign)}
                    className={`p-3 rounded-xl text-center transition-all ${
                      selectedSign?.name === sign.name 
                        ? 'bg-gradient-to-br from-[#9D4EDD] to-[#FF6B9D] text-white shadow-lg scale-105' 
                        : 'bg-[#f8f5ff] hover:bg-[#9D4EDD]/20'
                    }`}
                    data-testid={`zodiac-${sign.name.toLowerCase()}`}
                  >
                    <span className="text-2xl block" style={{ color: selectedSign?.name === sign.name ? 'white' : sign.color }}>{sign.symbol}</span>
                    <span className="text-xs mt-1 block">{sign.name}</span>
                  </button>
                ))}
              </div>
              {selectedSign && (
                <div className="mt-8 p-6 bg-gradient-to-r from-[#f8f5ff] to-[#fff5f8] rounded-xl" data-testid="zodiac-reading">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-4xl" style={{ color: selectedSign.color }}>{selectedSign.symbol}</span>
                    <div>
                      <h3 className="font-serif text-2xl text-[#1e1919]">{selectedSign.name}</h3>
                      <p className="text-sm text-[#7A7A9A]">{selectedSign.dates} &bull; {selectedSign.element}</p>
                    </div>
                  </div>
                  <p className="text-[#4A4A6A] leading-relaxed mb-4">{selectedSign.daily_message}</p>
                  <div className="flex items-center gap-2 text-sm text-[#9D4EDD]">
                    <Gem className="w-4 h-4" />
                    <span>Power Crystal: <strong>{selectedSign.crystal}</strong></span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'guidance' && !cosmicData && (
          <div className="text-center py-20 text-[#7A7A9A]">Loading cosmic guidance...</div>
        )}

        {/* ===== FENG SHUI TAB ===== */}
        {activeTab === 'fengshui' && (
          <div className="space-y-10" data-testid="fengshui-tab-content">

            {/* Authority Header */}
            <div className="bg-gradient-to-r from-[#1e1919] to-[#2a2030] rounded-2xl p-10 text-white text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a96e]/20 border border-[#c9a96e]/40 mb-4">
                <Compass className="w-4 h-4 text-[#c9a96e]" />
                <span className="text-xs text-[#c9a96e] tracking-[3px] uppercase font-semibold">America's Premier Feng Shui Authority</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl mb-4 text-white">Crystal-Integrated <span className="text-[#c9a96e]">Feng Shui</span></h2>
              <p className="text-white/90 max-w-2xl mx-auto text-lg leading-relaxed">
                Nationally recognized and internationally certified. Our Feng Shui consultants have transformed over 4,000 homes
                and commercial spaces across 38 states and 12 countries. We are the only consultancy in the United States that
                integrates authentic crystal energy mapping with classical Feng Shui principles.
              </p>
              <div className="flex flex-wrap justify-center gap-8 mt-8 text-white/80">
                <div><p className="text-2xl font-serif text-[#c9a96e]">4,000+</p><p className="text-xs uppercase tracking-wider">Homes Transformed</p></div>
                <div><p className="text-2xl font-serif text-[#c9a96e]">38</p><p className="text-xs uppercase tracking-wider">States Served</p></div>
                <div><p className="text-2xl font-serif text-[#c9a96e]">12</p><p className="text-xs uppercase tracking-wider">Countries</p></div>
                <div><p className="text-2xl font-serif text-[#c9a96e]">22 yrs</p><p className="text-xs uppercase tracking-wider">Experience</p></div>
              </div>
            </div>

            {/* SEO Content Section 1 */}
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-[#e8e4de]">
              <h2 className="font-serif text-2xl text-[#1e1919] mb-6">What Is Crystal Feng Shui and Why Does Your Home Need It?</h2>
              <div className="prose max-w-none text-[#4A4A6A] leading-relaxed space-y-4">
                <p>
                  Feng Shui, the ancient Chinese art of spatial harmonization, has been practiced for over 3,000 years to align human
                  environments with the natural flow of life force energy, known as <em>Chi</em>. At Gemstone Minerals, we have pioneered a
                  revolutionary approach that merges classical Feng Shui compass school methodology with the vibrational science of healing
                  crystals, creating what the industry now recognizes as <strong>Crystal-Integrated Feng Shui</strong> &mdash; a practice we
                  brought to the United States and have refined over two decades of professional consultation.
                </p>
                <p>
                  Every home accumulates stagnant energy over time. Negative Chi pools in cluttered corners, blocked doorways, and
                  misaligned furniture arrangements, leading to financial stagnation, relationship tension, health challenges, and creative
                  blocks. Our nationally certified Feng Shui consultants conduct comprehensive energy audits of your property, mapping
                  every room against the Bagua &mdash; the Feng Shui energy map &mdash; to identify precisely where Chi is blocked, where it
                  flows too rapidly, and where strategic crystal placement can restore perfect balance.
                </p>
                <p>
                  Unlike conventional interior designers who focus solely on aesthetics, our consultants analyze the electromagnetic
                  signatures of your living space, the orientation of your home relative to cardinal directions, the Five Element balance
                  (Wood, Fire, Earth, Metal, Water) within each room, and the unique energetic blueprint of your family. The result is a
                  home that doesn't just look beautiful &mdash; it actively supports your health, attracts prosperity, deepens your
                  relationships, and accelerates your personal evolution.
                </p>
              </div>
            </div>

            {/* Interactive Bagua Map */}
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-[#e8e4de]" data-testid="bagua-map-section">
              <div className="text-center mb-8">
                <h2 className="font-serif text-2xl text-[#1e1919] mb-2">Interactive Bagua Energy Map</h2>
                <p className="text-[#61525a]">Click any zone to discover its energy, recommended crystals, and Feng Shui enhancements</p>
              </div>
              <div className="grid grid-cols-3 gap-2 max-w-xl mx-auto mb-8">
                {[
                  { zone: 'wealth', label: 'Wealth & Prosperity', element: 'Wood', color: '#6B21A8', crystal: 'Citrine & Green Aventurine', direction: 'Southeast', tip: 'Place a citrine cluster in the far-left corner of your home from the main entrance. Add a small water fountain to activate wealth Chi. Avoid clutter, broken items, or trash cans in this zone.', icon: '$$' },
                  { zone: 'fame', label: 'Fame & Reputation', element: 'Fire', color: '#DC2626', crystal: 'Carnelian & Red Jasper', direction: 'South', tip: 'Activate this zone with candles, red accents, and a carnelian pyramid. Display awards, diplomas, or vision boards here. Proper lighting is essential for recognition energy.', icon: '\u2605' },
                  { zone: 'love', label: 'Love & Relationships', element: 'Earth', color: '#DB2777', crystal: 'Rose Quartz & Rhodonite', direction: 'Southwest', tip: 'Always use pairs in this zone: two rose quartz hearts, two candles, paired artwork. Remove single-figure imagery. Place a large rose quartz sphere on the nightstand to invite unconditional love.', icon: '\u2665' },
                  { zone: 'family', label: 'Family & Health', element: 'Wood', color: '#059669', crystal: 'Green Jade & Moss Agate', direction: 'East', tip: 'Fresh plants with rounded leaves thrive here. Place family photos in wooden frames. A green jade carving strengthens family bonds and promotes physical vitality through this zone.', icon: '\u263A' },
                  { zone: 'center', label: 'Health & Balance', element: 'Earth', color: '#D97706', crystal: 'Clear Quartz & Yellow Jasper', direction: 'Center', tip: 'The Tai Chi point of your home. Keep this area open, bright, and clutter-free. A large clear quartz generator placed here amplifies all other zones. Earth tones and natural materials ground the energy.', icon: '\u2609' },
                  { zone: 'children', label: 'Creativity & Children', element: 'Metal', color: '#7C3AED', crystal: 'Moonstone & Howlite', direction: 'West', tip: 'Metal wind chimes, white decor, and creative displays energize this zone. Moonstone supports fertility and creative inspiration. Keep this space playful, bright, and inspiring.', icon: '\u2728' },
                  { zone: 'knowledge', label: 'Knowledge & Wisdom', element: 'Earth', color: '#1D4ED8', crystal: 'Lapis Lazuli & Amethyst', direction: 'Northeast', tip: 'Create a meditation corner or reading nook here. Place lapis lazuli near bookshelves and an amethyst cluster on your study desk. Earth-tone colors and mountain imagery enhance wisdom energy.', icon: '\u{1F4D6}' },
                  { zone: 'career', label: 'Career & Life Path', element: 'Water', color: '#1E293B', crystal: 'Black Tourmaline & Obsidian', direction: 'North', tip: 'Water features like tabletop fountains activate career Chi. Place black tourmaline near your front door for protection and clarity. Mirror placement here can double career opportunities when done correctly.', icon: '\u2191' },
                  { zone: 'travel', label: 'Travel & Mentors', element: 'Metal', color: '#64748B', crystal: 'Selenite & Clear Quartz', direction: 'Northwest', tip: 'Photos of mentors, travel destinations, and spiritual teachers empower this zone. A selenite tower by the entrance invites helpful people and guardian energy into your life.', icon: '\u2708' },
                ].map((item) => (
                  <button
                    key={item.zone}
                    onClick={() => setActiveBaguaZone(activeBaguaZone?.zone === item.zone ? null : item)}
                    className={`relative p-4 rounded-xl text-center transition-all hover:scale-[1.03] ${
                      activeBaguaZone?.zone === item.zone
                        ? 'ring-2 ring-offset-2 shadow-xl scale-[1.03]'
                        : 'hover:shadow-md'
                    }`}
                    style={{
                      backgroundColor: activeBaguaZone?.zone === item.zone ? item.color : `${item.color}15`,
                      color: activeBaguaZone?.zone === item.zone ? 'white' : item.color,
                      ringColor: item.color,
                    }}
                    data-testid={`bagua-zone-${item.zone}`}
                  >
                    <span className="text-2xl block mb-1">{item.icon}</span>
                    <span className="text-xs font-semibold leading-tight block">{item.label}</span>
                    <span className="text-[9px] opacity-60 block mt-0.5">{item.direction}</span>
                  </button>
                ))}
              </div>

              {activeBaguaZone && (
                <div className="bg-gradient-to-r from-[#f8f5ff] to-[#fff5f8] rounded-xl p-6 md:p-8 border border-[#9D4EDD]/20" data-testid="bagua-zone-detail">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl" style={{ backgroundColor: `${activeBaguaZone.color}20` }}>
                      {activeBaguaZone.icon}
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-[#1e1919]">{activeBaguaZone.label}</h3>
                      <div className="flex flex-wrap gap-3 mt-1 text-sm">
                        <span className="text-[#7A7A9A]">Element: <strong className="text-[#1e1919]">{activeBaguaZone.element}</strong></span>
                        <span className="text-[#7A7A9A]">Direction: <strong className="text-[#1e1919]">{activeBaguaZone.direction}</strong></span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-white/80 rounded-lg">
                    <Gem className="w-4 h-4 text-[#9D4EDD]" />
                    <span className="text-sm text-[#1e1919]">Recommended Crystals: <strong>{activeBaguaZone.crystal}</strong></span>
                  </div>
                  <p className="text-[#4A4A6A] leading-relaxed">{activeBaguaZone.tip}</p>
                </div>
              )}
            </div>

            {/* SEO Content Section 2 */}
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-[#e8e4de]">
              <h2 className="font-serif text-2xl text-[#1e1919] mb-6">The Five Elements: Foundation of Crystal Feng Shui Mastery</h2>
              <div className="prose max-w-none text-[#4A4A6A] leading-relaxed space-y-4">
                <p>
                  Classical Feng Shui is governed by the interplay of five elemental energies: <strong>Wood</strong> (growth, vitality),
                  <strong> Fire</strong> (passion, visibility), <strong>Earth</strong> (stability, nourishment), <strong>Metal</strong> (precision, clarity),
                  and <strong>Water</strong> (flow, wisdom). Every room in your home has a dominant elemental signature, and when these elements
                  are balanced, the Chi flows harmoniously, supporting every aspect of your life.
                </p>
                <p>
                  Our proprietary assessment system, developed over 22 years of field research, maps each room's elemental composition using
                  both traditional compass readings and modern electromagnetic measurements. We then prescribe specific crystals that resonate
                  with the required element. For instance, a bedroom in the Southwest (Earth element) sector that suffers from relationship
                  discord may benefit from a rose quartz sphere combined with paired yellow jasper stones &mdash; grounding the Earth element
                  while opening the heart chakra simultaneously.
                </p>
                <p>
                  This level of precision is what separates our consultancy from generic Feng Shui advice. We don't offer cookie-cutter
                  solutions. Every crystal we prescribe, every furniture rearrangement we recommend, every color palette we suggest is calibrated
                  to <em>your</em> home, <em>your</em> family's energy, and <em>your</em> specific life goals. This is bespoke spatial healing at the
                  highest level &mdash; and it is the reason we are recognized as the foremost Feng Shui authority in the country.
                </p>
              </div>
            </div>

            {/* Five Elements Display */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { name: 'Wood', color: '#059669', desc: 'Growth & Vitality', crystal: 'Green Aventurine' },
                { name: 'Fire', color: '#DC2626', desc: 'Passion & Fame', crystal: 'Carnelian' },
                { name: 'Earth', color: '#D97706', desc: 'Stability & Love', crystal: 'Yellow Jasper' },
                { name: 'Metal', color: '#94A3B8', desc: 'Precision & Joy', crystal: 'Clear Quartz' },
                { name: 'Water', color: '#1D4ED8', desc: 'Wisdom & Career', crystal: 'Black Obsidian' },
              ].map((el) => (
                <div key={el.name} className="bg-white rounded-xl p-5 text-center border border-[#e8e4de] hover:shadow-lg transition-all">
                  <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${el.color}15` }}>
                    <div className="w-6 h-6 rounded-full" style={{ backgroundColor: el.color }} />
                  </div>
                  <h4 className="font-serif text-lg text-[#1e1919]">{el.name}</h4>
                  <p className="text-xs text-[#7A7A9A] mb-1">{el.desc}</p>
                  <p className="text-xs text-[#9D4EDD] font-semibold">{el.crystal}</p>
                </div>
              ))}
            </div>

            {/* SEO Content Section 3 */}
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-[#e8e4de]">
              <h2 className="font-serif text-2xl text-[#1e1919] mb-6">What to Expect From a Private Feng Shui Consultation</h2>
              <div className="prose max-w-none text-[#4A4A6A] leading-relaxed space-y-4">
                <p>
                  When you book a private Feng Shui home setup or remodel with Gemstone Minerals, you are not hiring a decorator &mdash;
                  you are commissioning a complete energetic transformation of your living environment. Our process begins with a comprehensive
                  2 to 4 hour in-home assessment, during which our lead consultant surveys every room, takes compass readings from multiple
                  points, interviews family members about their health, career, and relationship goals, and identifies all sources of
                  <em> Sha Chi</em> (negative energy arrows) both inside and surrounding the property.
                </p>
                <p>
                  Following the assessment, you receive a detailed Feng Shui blueprint: a room-by-room guide with precise crystal placement
                  coordinates, color recommendations, furniture repositioning instructions, and element-balancing prescriptions. Our premium
                  clients also receive a curated crystal kit, hand-selected by our gemologists specifically for your home's energy profile.
                  We return for a second visit to oversee implementation and perform a final energy verification.
                </p>
                <p>
                  Our clients consistently report remarkable shifts within the first 30 days: unexpected financial windfalls, deepened
                  intimacy in relationships, resolution of chronic health issues, and a profound sense of peace and alignment that permeates
                  every corner of their home. These are not coincidences &mdash; they are the measurable results of properly harmonized Chi
                  flowing through a space that has been meticulously calibrated by experts who have dedicated their lives to this art.
                </p>
              </div>
            </div>

            {/* Pricing & Consultation CTA */}
            <div className="bg-gradient-to-br from-[#1e1919] via-[#2a2030] to-[#1e1919] rounded-3xl p-10 text-white overflow-hidden relative" data-testid="fengshui-consultation-section">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-5 right-10 w-60 h-60 bg-[#c9a96e]/40 rounded-full blur-3xl" />
              </div>
              <div className="relative z-10 max-w-3xl mx-auto">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a96e]/20 border border-[#c9a96e]/30 mb-4">
                    <Home className="w-4 h-4 text-[#c9a96e]" />
                    <span className="text-xs text-[#c9a96e] tracking-[2px] uppercase font-semibold">Private Consultation</span>
                  </div>
                  <h2 className="font-serif text-3xl mb-4">Transform Your Home Into a <span className="text-[#c9a96e]">Sanctuary</span></h2>
                  <p className="text-white/70 max-w-xl mx-auto">
                    Our internationally certified Feng Shui consultants are ready to guide you through a complete energetic
                    transformation of your living space.
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {[
                    { sqft: 'Up to 2,200 sq ft', price: '$6,000' },
                    { sqft: '2,201 - 3,500 sq ft', price: '$9,000' },
                    { sqft: '3,501 - 5,000 sq ft', price: '$14,000' },
                    { sqft: '5,001+ sq ft', price: '$20,000' },
                  ].map((tier) => (
                    <div key={tier.sqft} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                      <p className="text-xs text-white/60 mb-1">{tier.sqft}</p>
                      <p className="font-serif text-xl text-[#c9a96e]">{tier.price}</p>
                      <p className="text-[10px] text-white/50 mt-1">Minimum Deposit</p>
                    </div>
                  ))}
                </div>
                {!showFengShuiForm && !fengShuiSubmitted && (
                  <div className="text-center">
                    <button
                      onClick={() => { setShowFengShuiForm(true); setTimeout(() => document.getElementById('fengshui-form')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
                      className="px-10 py-4 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-[#c9a96e]/30 hover:scale-105 transition-all"
                      data-testid="fengshui-request-btn"
                    >
                      Request Private Consultation
                    </button>
                    <p className="text-xs text-white/50 mt-3">In-home assessments available nationwide. Virtual consultations for international clients.</p>
                  </div>
                )}

                {fengShuiSubmitted && (
                  <div className="text-center py-6" data-testid="fengshui-success">
                    <div className="w-16 h-16 rounded-full bg-[#00C853]/20 flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-[#00C853]" /></div>
                    <h3 className="font-serif text-2xl mb-2">Consultation Request Received</h3>
                    <p className="text-white/60 max-w-md mx-auto">
                      A senior Feng Shui consultant will contact you within 24 hours to schedule your in-home assessment.
                      Please check your email for confirmation details.
                    </p>
                  </div>
                )}

                {showFengShuiForm && !fengShuiSubmitted && (
                  <form onSubmit={handleFsSubmit} id="fengshui-form" className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8" data-testid="fengshui-form">
                    <h3 className="font-serif text-xl text-center mb-6">Private Feng Shui Consultation Request</h3>
                    <div className="space-y-3">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <input type="text" name="fullName" placeholder="Full Name *" value={fsForm.fullName} onChange={handleFsChange} required className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:ring-2 focus:ring-[#c9a96e] outline-none text-sm" data-testid="fs-fullname" />
                        <input type="email" name="email" placeholder="Email *" value={fsForm.email} onChange={handleFsChange} required className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:ring-2 focus:ring-[#c9a96e] outline-none text-sm" data-testid="fs-email" />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <input type="tel" name="phone" placeholder="Phone *" value={fsForm.phone} onChange={handleFsChange} required className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:ring-2 focus:ring-[#c9a96e] outline-none text-sm" data-testid="fs-phone" />
                        <input type="text" name="address" placeholder="Property Address *" value={fsForm.address} onChange={handleFsChange} required className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:ring-2 focus:ring-[#c9a96e] outline-none text-sm" data-testid="fs-address" />
                      </div>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <input type="text" name="city" placeholder="City *" value={fsForm.city} onChange={handleFsChange} required className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:ring-2 focus:ring-[#c9a96e] outline-none text-sm" data-testid="fs-city" />
                        <input type="text" name="state" placeholder="State *" value={fsForm.state} onChange={handleFsChange} required className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:ring-2 focus:ring-[#c9a96e] outline-none text-sm" data-testid="fs-state" />
                        <input type="text" name="zipCode" placeholder="Zip Code *" value={fsForm.zipCode} onChange={handleFsChange} required className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:ring-2 focus:ring-[#c9a96e] outline-none text-sm" data-testid="fs-zip" />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-white/50 mb-1 block">Square Footage *</label>
                          <input type="number" name="squareFootage" placeholder="2200" value={fsForm.squareFootage} onChange={handleFsChange} required min="500" className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:ring-2 focus:ring-[#c9a96e] outline-none text-sm" data-testid="fs-sqft" />
                          <p className="text-xs text-[#c9a96e] mt-1">Estimated deposit: ${getDeposit(fsForm.squareFootage).toLocaleString()}</p>
                        </div>
                        <div>
                          <label className="text-xs text-white/50 mb-1 block">Home Type *</label>
                          <select name="homeType" value={fsForm.homeType} onChange={handleFsChange} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:ring-2 focus:ring-[#c9a96e] outline-none text-sm" data-testid="fs-hometype">
                            <option value="Single Family" className="text-black">Single Family</option>
                            <option value="Condo/Apartment" className="text-black">Condo/Apartment</option>
                            <option value="Townhouse" className="text-black">Townhouse</option>
                            <option value="Multi-Family" className="text-black">Multi-Family</option>
                            <option value="Commercial" className="text-black">Commercial</option>
                            <option value="Estate/Mansion" className="text-black">Estate/Mansion</option>
                          </select>
                        </div>
                      </div>
                      <input type="text" name="rooms" placeholder="Number & types of rooms (e.g. 3BR, 2BA, Office, Den) *" value={fsForm.rooms} onChange={handleFsChange} required className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:ring-2 focus:ring-[#c9a96e] outline-none text-sm" data-testid="fs-rooms" />
                      <textarea name="primaryGoals" placeholder="What are your primary goals? (e.g. improve finances, strengthen marriage, better sleep, career advancement) *" value={fsForm.primaryGoals} onChange={handleFsChange} required rows={3} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:ring-2 focus:ring-[#c9a96e] outline-none text-sm resize-none" data-testid="fs-goals" />
                      <textarea name="currentChallenges" placeholder="Current energy challenges in your home? (optional)" value={fsForm.currentChallenges} onChange={handleFsChange} rows={2} className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:ring-2 focus:ring-[#c9a96e] outline-none text-sm resize-none" data-testid="fs-challenges" />
                      <button type="submit" disabled={fengShuiSubmitting} className="w-full py-4 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold text-base hover:shadow-2xl hover:shadow-[#c9a96e]/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-testid="fs-submit-btn">
                        {fengShuiSubmitting ? <><div className="w-5 h-5 border-2 border-[#1e1919]/30 border-t-[#1e1919] rounded-full animate-spin" /> Processing...</> : <><Send className="w-5 h-5" /> Request Consultation &mdash; ${getDeposit(fsForm.squareFootage).toLocaleString()} Deposit</>}
                      </button>
                      <p className="text-xs text-center text-white/50">A consultant will contact you within 24 hours. Deposit is collected upon scheduling confirmation.</p>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== NEWS TAB ===== */}
        {activeTab === 'news' && !selectedArticle && (
          <div className="space-y-6" data-testid="news-tab-content">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-serif text-2xl text-[#1e1919]">Metaphysical News &amp; Insights</h2>
              <a href={`${API_URL}/api/rss`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#c9a96e]/10 text-[#c9a96e] text-sm font-semibold hover:bg-[#c9a96e]/20 transition-all" data-testid="rss-subscribe-btn">
                <Rss className="w-4 h-4" /> Subscribe via RSS
              </a>
            </div>
            {articles.length > 0 ? articles.map((article) => (
              <article key={article.id} onClick={() => openArticle(article.slug)} className="bg-white rounded-xl p-6 shadow-sm border border-[#e8e4de] hover:shadow-lg hover:border-[#9D4EDD]/30 transition-all cursor-pointer" data-testid={`article-card-${article.slug}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-2 py-1 rounded-full bg-[#9D4EDD]/10 text-[#9D4EDD] text-xs font-semibold">{article.category}</span>
                      {article.trending && (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#FF6B9D]/10 text-[#FF6B9D] text-xs font-semibold">
                          <TrendingUp className="w-3 h-3" /> Trending
                        </span>
                      )}
                      <span className="text-xs text-[#7A7A9A]">{article.word_count} words</span>
                    </div>
                    <h3 className="font-serif text-lg text-[#1e1919] mb-2 hover:text-[#9D4EDD] transition-colors">{article.title}</h3>
                    <p className="text-sm text-[#61525a] mb-3 line-clamp-2">{article.summary}</p>
                    <div className="flex items-center gap-4 text-sm text-[#7A7A9A]">
                      <span>{article.source}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(article.published_at)}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#c9a96e] flex-shrink-0 mt-2" />
                </div>
              </article>
            )) : (
              <div className="text-center py-12 text-[#7A7A9A]">Loading articles...</div>
            )}
            <div className="text-center pt-8">
              <p className="text-[#7A7A9A] text-sm">
                All articles are SEO-optimized and available via our{' '}
                <a href={`${API_URL}/api/rss`} target="_blank" rel="noopener noreferrer" className="text-[#9D4EDD] underline">RSS feed</a>
              </p>
            </div>
          </div>
        )}

        {/* ===== ARTICLE DETAIL VIEW ===== */}
        {activeTab === 'news' && selectedArticle && (
          <div data-testid="article-detail">
            <button onClick={closeArticle} className="flex items-center gap-2 text-sm text-[#9D4EDD] hover:text-[#FF6B9D] transition-colors mb-8" data-testid="article-back-btn">
              <ArrowLeft className="w-4 h-4" /> Back to all articles
            </button>
            <article className="bg-white rounded-2xl shadow-lg border border-[#e8e4de] overflow-hidden">
              <div className="bg-gradient-to-r from-[#1e1919] to-[#2a2030] p-8 md:p-12">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full bg-[#9D4EDD]/20 text-[#9D4EDD] text-xs font-bold uppercase">{selectedArticle.category}</span>
                  {selectedArticle.trending && (
                    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-[#FF6B9D]/20 text-[#FF6B9D] text-xs font-bold"><TrendingUp className="w-3 h-3" /> Trending</span>
                  )}
                </div>
                <h1 className="font-serif text-2xl md:text-3xl text-white mb-4 leading-tight">{selectedArticle.title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-white/50 text-sm">
                  <span className="flex items-center gap-1"><Newspaper className="w-3.5 h-3.5" /> {selectedArticle.source}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {formatDate(selectedArticle.published_at)}</span>
                  <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {selectedArticle.word_count} words</span>
                </div>
              </div>
              <div className="p-8 md:p-12">
                <p className="text-lg text-[#9D4EDD] font-medium mb-8 leading-relaxed border-l-4 border-[#9D4EDD] pl-6">{selectedArticle.summary}</p>
                <div className="prose prose-lg max-w-none text-[#4A4A6A] leading-relaxed">
                  {selectedArticle.content.split('\n\n').map((paragraph, i) => (
                    paragraph.trim() && <p key={i} className="mb-6">{paragraph.trim()}</p>
                  ))}
                </div>
              </div>
              <div className="border-t border-[#e8e4de] p-8 bg-gradient-to-r from-[#f8f5ff] to-[#fff5f8]">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Rss className="w-5 h-5 text-[#c9a96e]" />
                    <span className="text-sm text-[#61525a]">Get articles like this in your RSS reader</span>
                  </div>
                  <a href={`${API_URL}/api/rss`} target="_blank" rel="noopener noreferrer" className="px-6 py-2 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl text-sm font-bold hover:shadow-lg transition-all">
                    Subscribe to RSS Feed
                  </a>
                </div>
              </div>
            </article>
          </div>
        )}

        {/* ===== DIRECTORY TAB ===== */}
        {activeTab === 'directory' && (
          <div className="space-y-8">
            <div className="bg-gradient-to-r from-[#1e1919] to-[#2a2525] rounded-2xl p-8 text-white">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h2 className="font-serif text-2xl mb-2">Metaphysical Business Directory</h2>
                  <p className="text-white/70">Connect with trusted healers, shops, and practitioners worldwide</p>
                </div>
                <div className="flex gap-3">
                  <Link to="/directory" className="px-6 py-3 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-semibold hover:shadow-lg transition-all">List Your Business</Link>
                  <Link to="/directory" className="px-6 py-3 bg-white/10 border border-white/20 rounded-xl font-semibold hover:bg-white/20 transition-all">View Full Directory</Link>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7A7A9A]" />
                <input type="text" placeholder="Search businesses, services, locations..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none" data-testid="directory-search" />
              </div>
              <button className="flex items-center gap-2 px-6 py-3 bg-[#f8f5ff] rounded-xl text-[#61525a] hover:bg-[#9D4EDD]/20 transition-all">
                <Filter className="w-4 h-4" /> Filters
              </button>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4"><Crown className="w-5 h-5 text-[#c9a96e]" /><h3 className="font-serif text-xl text-[#1e1919]">Featured Listings</h3></div>
              <div className="grid md:grid-cols-3 gap-6">
                {directoryListings.filter(l => l.featured).map((listing) => (
                  <div key={listing.id} className="bg-white rounded-xl p-6 shadow-lg border-2 border-[#c9a96e]/30 hover:border-[#c9a96e] transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <span className="px-2 py-1 rounded-full bg-[#c9a96e]/20 text-[#c9a96e] text-xs font-bold uppercase">Featured</span>
                      <div className="flex items-center gap-1 text-[#FFB800]"><Star className="w-4 h-4 fill-current" /><span className="font-semibold">{listing.rating}</span></div>
                    </div>
                    <h4 className="font-serif text-lg text-[#1e1919] mb-1">{listing.name}</h4>
                    <p className="text-sm text-[#9D4EDD] mb-2">{listing.category}</p>
                    <div className="flex items-center gap-2 text-sm text-[#7A7A9A] mb-3"><MapPin className="w-4 h-4" />{listing.location}</div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {listing.services.map((s, i) => (<span key={i} className="px-2 py-1 rounded-full bg-[#f8f5ff] text-[#61525a] text-xs">{s}</span>))}
                    </div>
                    <button className="w-full py-2 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white rounded-lg text-sm font-semibold hover:shadow-lg transition-all">View Profile</button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-serif text-xl text-[#1e1919] mb-4">All Listings</h3>
              <div className="space-y-4">
                {directoryListings.filter(l => !l.featured).map((listing) => (
                  <div key={listing.id} className="bg-white rounded-xl p-4 border border-[#e8e4de] hover:shadow-md transition-all flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#f8f5ff] flex items-center justify-center"><Store className="w-6 h-6 text-[#9D4EDD]" /></div>
                      <div><h4 className="font-semibold text-[#1e1919]">{listing.name}</h4><p className="text-sm text-[#7A7A9A]">{listing.category} &bull; {listing.location}</p></div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1 text-[#FFB800]"><Star className="w-4 h-4 fill-current" /><span>{listing.rating}</span></div>
                      <ExternalLink className="w-5 h-5 text-[#c9a96e]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#f8f5ff] to-[#fff5f8] rounded-2xl p-8 text-center">
              <Crown className="w-10 h-10 text-[#c9a96e] mx-auto mb-4" />
              <h3 className="font-serif text-2xl text-[#1e1919] mb-2">Get Your Business Featured</h3>
              <p className="text-[#61525a] mb-6 max-w-xl mx-auto">Featured listings get 10x more visibility. Join our directory and reach thousands of spiritual seekers.</p>
              <Link to="/directory" className="inline-block px-8 py-3 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-semibold hover:shadow-lg transition-all" data-testid="directory-cta-btn">
                Apply for $150/month Listing
              </Link>
            </div>
          </div>
        )}

        {/* ===== EVENTS TAB ===== */}
        {activeTab === 'events' && (
          <div className="space-y-8" data-testid="events-tab-content">
            {/* Featured Events */}
            {featuredEvents.length > 0 && featuredEvents.map(event => (
              <div key={event.event_id} className="bg-gradient-to-r from-[#1e1919] to-[#2a2525] rounded-2xl p-8 text-white" data-testid={`featured-event-${event.event_id}`}>
                <span className="px-3 py-1 rounded-full bg-[#c9a96e] text-[#1e1919] text-xs font-bold uppercase mb-4 inline-block">Featured Event</span>
                <h3 className="font-serif text-3xl mb-2">{event.title}</h3>
                <p className="text-white/70 mb-4">{event.description}</p>
                <div className="flex flex-wrap gap-6 mb-6 text-white/80">
                  <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> {formatDate(event.date)}</span>
                  <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> {event.time}</span>
                  <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> {event.event_type}</span>
                </div>
                <div className="flex items-center gap-4">
                  {!event.free && <span className="font-serif text-3xl text-[#c9a96e]">${event.price}</span>}
                  {event.event_id === 'selenite-healing-mastery' ? (
                    <Link to="/webinar-registration" className="px-8 py-3 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-semibold hover:shadow-lg transition-all">Reserve Your Seat</Link>
                  ) : (
                    <button className="px-8 py-3 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-semibold hover:shadow-lg transition-all">Learn More</button>
                  )}
                </div>
              </div>
            ))}

            {/* Calendar View */}
            <div className="bg-white rounded-2xl shadow-lg border border-[#e8e4de] overflow-hidden" data-testid="events-calendar">
              <div className="bg-gradient-to-r from-[#9D4EDD]/10 to-[#FF6B9D]/10 p-6 flex items-center justify-between">
                <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-white/60 transition-all" data-testid="calendar-prev"><ChevronLeft className="w-5 h-5 text-[#61525a]" /></button>
                <h3 className="font-serif text-xl text-[#1e1919]">{MONTH_NAMES[calendarMonth]} {calendarYear}</h3>
                <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-white/60 transition-all" data-testid="calendar-next"><ChevronRight className="w-5 h-5 text-[#61525a]" /></button>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                    <div key={d} className="text-center text-xs font-semibold text-[#7A7A9A] py-2">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: getFirstDayOfMonth(calendarMonth, calendarYear) }).map((_, i) => (
                    <div key={`empty-${i}`} className="h-16" />
                  ))}
                  {Array.from({ length: getDaysInMonth(calendarMonth, calendarYear) }).map((_, i) => {
                    const day = i + 1;
                    const dayEvents = getEventsForDate(day);
                    const isToday = day === new Date().getDate() && calendarMonth === new Date().getMonth() && calendarYear === new Date().getFullYear();
                    return (
                      <div key={day} className={`h-16 rounded-lg p-1 text-sm relative transition-all ${isToday ? 'bg-[#9D4EDD]/10 ring-2 ring-[#9D4EDD]' : 'hover:bg-[#f8f5ff]'} ${dayEvents.length > 0 ? 'cursor-pointer' : ''}`}>
                        <span className={`text-xs ${isToday ? 'font-bold text-[#9D4EDD]' : 'text-[#61525a]'}`}>{day}</span>
                        {dayEvents.map((ev, idx) => (
                          <div key={idx} className={`text-[9px] leading-tight mt-0.5 px-1 rounded truncate ${ev.free ? 'bg-[#00C853]/20 text-[#00C853]' : 'bg-[#9D4EDD]/20 text-[#9D4EDD]'}`} title={ev.title}>
                            {ev.title.length > 12 ? ev.title.substring(0, 12) + '...' : ev.title}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {eventCategories.map(cat => (
                <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${selectedCategory === cat ? 'bg-[#9D4EDD] text-white' : 'bg-[#f8f5ff] text-[#61525a] hover:bg-[#9D4EDD]/10'}`} data-testid={`event-filter-${cat.toLowerCase()}`}>
                  {cat}
                </button>
              ))}
            </div>

            {/* Events List */}
            <div>
              <h3 className="font-serif text-xl text-[#1e1919] mb-4">All Upcoming Events</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {sortedEvents.map((event) => (
                  <div key={event.event_id} className="bg-white rounded-xl p-6 border border-[#e8e4de] hover:shadow-lg transition-all" data-testid={`event-card-${event.event_id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${event.free ? 'bg-[#00C853]/20 text-[#00C853]' : 'bg-[#9D4EDD]/20 text-[#9D4EDD]'}`}>
                          {event.free ? 'Free' : `$${event.price}`}
                        </span>
                        {event.category && (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-[#FFB800]/15 text-[#c9a96e]">{event.category}</span>
                        )}
                      </div>
                      <span className="text-sm text-[#7A7A9A]">{event.event_type}</span>
                    </div>
                    <h4 className="font-serif text-lg text-[#1e1919] mb-2">{event.title}</h4>
                    {event.description && <p className="text-sm text-[#61525a] mb-3 line-clamp-2">{event.description}</p>}
                    <div className="flex items-center gap-4 text-sm text-[#61525a]">
                      <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(event.date)}</span>
                      {event.time && <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{event.time}</span>}
                    </div>
                    {event.host && <p className="text-xs text-[#7A7A9A] mt-2">Hosted by {event.host}</p>}
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Event Section */}
            <div className="bg-[#f8f5ff] rounded-2xl p-8" id="submit-event-section" data-testid="submit-event-section">
              {!showEventForm && !eventFormSubmitted && (
                <div className="text-center">
                  <Calendar className="w-10 h-10 text-[#9D4EDD] mx-auto mb-4" />
                  <h3 className="font-serif text-xl text-[#1e1919] mb-2">Host a Spiritual Event?</h3>
                  <p className="text-[#61525a] mb-4">Get your workshops, retreats, and ceremonies in front of our engaged community</p>
                  <button onClick={() => setShowEventForm(true)} className="px-6 py-3 bg-[#9D4EDD] text-white rounded-xl font-semibold hover:shadow-lg transition-all" data-testid="submit-event-btn">Submit Your Event ($25)</button>
                </div>
              )}

              {eventFormSubmitted && (
                <div className="text-center py-6" data-testid="event-submit-success">
                  <div className="w-14 h-14 rounded-full bg-[#00C853]/10 flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-7 h-7 text-[#00C853]" /></div>
                  <h3 className="font-serif text-xl text-[#1e1919] mb-2">Event Submitted!</h3>
                  <p className="text-[#61525a] max-w-md mx-auto">Thank you! Your event has been submitted for review. We'll contact you within 48 hours. The $25 listing fee will be confirmed by email.</p>
                </div>
              )}

              {showEventForm && !eventFormSubmitted && (
                <form onSubmit={handleEventSubmit} data-testid="event-submission-form">
                  <div className="text-center mb-6">
                    <Calendar className="w-8 h-8 text-[#9D4EDD] mx-auto mb-2" />
                    <h3 className="font-serif text-xl text-[#1e1919]">Submit Your Event</h3>
                    <p className="text-sm text-[#7A7A9A]">$25 listing fee &bull; Reviewed within 48 hours</p>
                  </div>
                  <div className="space-y-3">
                    <input type="text" name="title" placeholder="Event Title *" value={eventForm.title} onChange={handleEventChange} required className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm" data-testid="event-title-input" />
                    <textarea name="description" placeholder="Event Description *" value={eventForm.description} onChange={handleEventChange} required rows={3} className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm resize-none" data-testid="event-description-input" />
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input type="date" name="date" value={eventForm.date} onChange={handleEventChange} required className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm" data-testid="event-date-input" />
                      <input type="text" name="time" placeholder="Time (e.g. 2:00 PM EST)" value={eventForm.time} onChange={handleEventChange} required className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm" data-testid="event-time-input" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <select name="event_type" value={eventForm.event_type} onChange={handleEventChange} className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm" data-testid="event-type-select">
                        <option value="Online">Online</option>
                        <option value="In-Person">In-Person</option>
                        <option value="Online & In-Person">Online & In-Person</option>
                        <option value="Virtual Retreat">Virtual Retreat</option>
                        <option value="Live Webinar">Live Webinar</option>
                      </select>
                      <select name="category" value={eventForm.category} onChange={handleEventChange} className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm" data-testid="event-category-select">
                        <option value="Workshop">Workshop</option>
                        <option value="Ceremony">Ceremony</option>
                        <option value="Meditation">Meditation</option>
                        <option value="Retreat">Retreat</option>
                        <option value="Webinar">Webinar</option>
                        <option value="Masterclass">Masterclass</option>
                        <option value="Circle">Circle</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input type="text" name="host" placeholder="Host / Organization *" value={eventForm.host} onChange={handleEventChange} required className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm" data-testid="event-host-input" />
                      <input type="email" name="contact_email" placeholder="Contact Email *" value={eventForm.contact_email} onChange={handleEventChange} required className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#9D4EDD] focus:border-transparent outline-none text-sm" data-testid="event-email-input" />
                    </div>
                    <button type="submit" disabled={eventSubmitting} className="w-full py-3 bg-[#9D4EDD] text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-testid="event-form-submit-btn">
                      {eventSubmitting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Submitting...</> : <><Send className="w-4 h-4" /> Submit Event &mdash; $25 Listing Fee</>}
                    </button>
                    <p className="text-xs text-center text-[#7A7A9A]">Your event will be reviewed before publishing. Listing fee confirmed by email.</p>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MetaphysicalHubPage;
